import * as React from 'react';
import { AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  type AdminNotificationStackItem,
  type AdminNotificationSeverity,
  highestSeverity,
  issueCountLabel,
} from '@/lib/admin-notification-stack.types';

export type AdminNotificationStackProps = {
  notifications: AdminNotificationStackItem[];
  onResolve?: (id: string, item: AdminNotificationStackItem) => void | Promise<void>;
  onAction?: (item: AdminNotificationStackItem) => void;
  maxVisible?: number;
  className?: string;
  resolvingIds?: ReadonlySet<string>;
  /** Snooze requires settings.edit. Default true when onResolve is provided. */
  canSnooze?: boolean;
};

export { issueCountLabel };

export const BAR_HEIGHT = 32;
export const CARD_GAP_PX = 10;
export const BAR_STACK_GAP = 2;
/** Header quick stack shows at most this many cards before routing to the inbox page. */
export const HEADER_QUICK_STACK_LIMIT = 3;
/** Preferred dropdown width — wider than the badge so copy can breathe. */
export const HEADER_STACK_PANEL_WIDTH = 420;
const HEADER_STACK_VIEWPORT_GUTTER = 12;

export const cardListVariants = {
  open: { transition: { staggerChildren: 0.04, delayChildren: 0.02 } },
};

export const cardItemVariants = {
  closed: { opacity: 0, y: -10, scale: 0.98 },
  open: { opacity: 1, y: 0, scale: 1 },
};

export const severityStyles: Record<
  AdminNotificationSeverity,
  { icon: typeof AlertCircle; bar: string; badge: string; ring: string }
> = {
  critical: {
    icon: AlertCircle,
    bar: 'border-destructive/40 bg-destructive/5 text-destructive',
    badge: 'bg-destructive text-destructive-foreground',
    ring: 'ring-destructive/50',
  },
  warning: {
    icon: AlertTriangle,
    bar: 'border-warning/40 bg-warning/5 text-warning-text',
    badge: 'bg-warning text-warning-foreground',
    ring: 'ring-warning/40',
  },
  info: {
    icon: Info,
    bar: 'border-border bg-muted/40 text-foreground',
    badge: 'bg-secondary text-secondary-foreground',
    ring: 'ring-border',
  },
};

export const footerActionRowClass = 'grid shrink-0 grid-cols-2 border-t border-border bg-muted/40';

export const footerSnoozeClass =
  'h-9 w-full rounded-none justify-center text-xs font-medium hover:bg-muted/50';

export const footerFixClass =
  'h-9 w-full rounded-none justify-center text-xs font-medium border-l border-border hover:bg-muted/50';

export const footerFixCriticalClass =
  'h-9 w-full rounded-none justify-center text-xs font-medium border-l border-border text-destructive hover:bg-destructive/10';

export function runNotificationAction(
  item: AdminNotificationStackItem,
  onAction?: (item: AdminNotificationStackItem) => void,
) {
  if (item.onAction) {
    item.onAction();
    onAction?.(item);
    return;
  }
  if (!item.actionHref) return;
  onAction?.(item);
  if (/^https?:\/\//i.test(item.actionHref)) {
    window.open(item.actionHref, '_blank', 'noopener,noreferrer');
    return;
  }
  window.location.assign(item.actionHref);
}

type AnchorRect = { top: number; left: number; width: number };

export function useAnchorRect(ref: React.RefObject<HTMLElement | null>, expanded: boolean): AnchorRect | null {
  const [rect, setRect] = React.useState<AnchorRect | null>(null);

  const measure = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const maxWidth = Math.max(240, window.innerWidth - HEADER_STACK_VIEWPORT_GUTTER * 2);
    const width = Math.min(HEADER_STACK_PANEL_WIDTH, maxWidth);
    // Right-align under the badge so a wider panel does not spill past the viewport edge.
    const left = Math.min(
      Math.max(HEADER_STACK_VIEWPORT_GUTTER, r.right - width),
      window.innerWidth - HEADER_STACK_VIEWPORT_GUTTER - width,
    );
    setRect({ top: r.top, left, width });
  }, [ref]);

  React.useLayoutEffect(() => {
    measure();
  }, [measure, expanded]);

  React.useEffect(() => {
    if (!expanded) return;
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [expanded, measure]);

  return rect;
}

export function SeverityIcon({
  severity,
  className,
}: {
  severity: AdminNotificationSeverity;
  className?: string;
}) {
  const { icon: Icon } = severityStyles[severity];
  return <Icon className={cn('h-4 w-4 shrink-0', className)} aria-hidden />;
}

export function notificationBarSeverity(
  items: AdminNotificationStackItem[],
): AdminNotificationSeverity {
  return highestSeverity(items) ?? 'info';
}
